import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import {
  SettingLayout, SettingsCard, SettingsSectionHeader,
  SettingsSectionTitle, SettingsCountChip,
  SettingsFilterCard, SettingsSearchWrap, SettingsFormSelect,
} from "../Settings/SettingShell";
import { PrimaryButton } from "../../globalComponents/ui/Button";
import { useFinancialYears } from "../../hooks/useApiData";
import { usePermission } from "../../hooks/usePermission";
import { getLatestFY } from "../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import { listDocuments } from "../../_services/documentsService";
import DocumentList from "./DocumentList";
import DocumentUploadModal from "./DocumentUploadModal";

const ESGDocumentLibrary = (props) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState("");
  const [financialYearId, setFinancialYearId] = useState("");

  const financialYears = useFinancialYears();
  const [canUpload] = usePermission("policy-library.upload-policy", "LIST_POLICY");

  // Financial year always defaults to the latest one available.
  useEffect(() => {
    if (!financialYearId && financialYears.length) {
      const latest = getLatestFY(financialYears);
      if (latest) setFinancialYearId(String(latest.id));
    }
  }, [financialYears, financialYearId]);

  const fetchDocuments = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listDocuments({
        documentType: ["ESG_DOCUMENT_LIBRARY"],
        financialYearId: financialYearId || undefined,
      });
      setDocuments(result);
    } catch {
      setError("Something went wrong while fetching documents");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (financialYearId) fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYearId]);

  const filteredDocuments = useMemo(() => {
    if (!search.trim()) return documents;
    const q = search.trim().toLowerCase();
    return documents.filter((d) => (d.fileMetadata?.fileName || d.fileName || "").toLowerCase().includes(q));
  }, [documents, search]);

  return (
    <div className="d-flex flex-row mainclass" style={{ height: "100vh", overflow: "auto" }}>
      <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, zIndex: 999 }}>
        <Sidebar dataFromParent={props.location.pathname} onSidebarToggle={setSidebarExpanded} />
      </div>

      <div style={{ flex: "1", minWidth: 0, transition: "all 0.3s ease" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 999 }}><Header /></div>

        <SettingLayout
          title="Policy Library"
          subtitle={`${filteredDocuments.length} document${filteredDocuments.length === 1 ? "" : "s"}`}
          $wide
          rightContent={
            canUpload && (
              <PrimaryButton onClick={() => setShowUpload(true)}>
                <i className="fas fa-upload" style={{ marginRight: 6 }} />Upload Document
              </PrimaryButton>
            )
          }
        >
          <SettingsFilterCard>
            <SettingsSearchWrap style={{ flex: 2, minWidth: 200 }}>
              <i className="fas fa-search s-icon" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search documents by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SettingsSearchWrap>

            <div style={{ minWidth: 170 }}>
              <SettingsFormSelect value={financialYearId} onChange={(e) => setFinancialYearId(e.target.value)} style={{ height: 38 }}>
                {financialYears.map((fy) => (
                  <option key={fy.id} value={fy.id}>{fy.financial_year_value}</option>
                ))}
              </SettingsFormSelect>
            </div>
          </SettingsFilterCard>

          <SettingsCard style={{ marginTop: 16 }}>
            <SettingsSectionHeader>
              <i className="fas fa-folder-open" style={{ fontSize: 12, color: "#3f88a5" }} />
              <SettingsSectionTitle>Documents</SettingsSectionTitle>
              <SettingsCountChip>{filteredDocuments.length}</SettingsCountChip>
            </SettingsSectionHeader>
            <div style={{ padding: 16 }}>
              <DocumentList
                documents={filteredDocuments}
                financialYears={financialYears}
                isLoading={isLoading}
                error={error}
                onRetry={fetchDocuments}
                onRefresh={fetchDocuments}
              />
            </div>
          </SettingsCard>
        </SettingLayout>

        {canUpload && (
          <DocumentUploadModal
            show={showUpload}
            onClose={() => setShowUpload(false)}
            onSuccess={fetchDocuments}
            financialYears={financialYears}
            financialYearId={financialYearId}
          />
        )}
      </div>
    </div>
  );
};

export default ESGDocumentLibrary;
